<template>
  <el-dialog
    v-model="visible"
    :title="t('settlement.faceAuthTitle')"
    width="590px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <div class="face-verification">
      <p class="verification-title">{{t('settlement.chooseVerificationMethod')}}:</p>
      
      <!-- 方法一：手机扫码 -->
      <div class="verification-method">
        <h4>{{  t('settlement.method1')}}</h4>
        <div class="qrcode-container">
          <div class="qrcode">
            <!-- 二维码图片 -->
            <div class="qrcode-image" v-loading="loading">
              <img :src="qrCertifyUrl || require('@/assets/img/qr_code.png')" alt="二维码" />
            </div>
            <p class="qrcode-tip">{{  t('settlement.scanQRCodeTip')}}</p>
          </div>
        </div>
      </div>
      
      <!-- 方法二：电脑摄像头 -->
      <div class="verification-method">
        <h4>{{ t('settlement.method2') }}</h4>
        <div class="camera-container">
          <p class="camera-tip">{{ t('settlement.cameraTip') }}</p>
          <el-button type="primary" class="verify-button" @click="handleCameraVerify">{{ t('settlement.startVerification') }}</el-button>
        </div>
      </div>
      
      <!-- 认证说明 -->
      <div class="verification-notes">
        <p>• {{ t('settlement.verificationNote1') }}</p>
        <p>• {{ t('settlement.verificationNote2') }}</p>
      </div>
    </div>
    
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleCancel">{{ t('settlement.cancel')}}</el-button>
        <el-button type="primary" @click="handleVerificationComplete">{{ t('settlement.verificationComplete') }}</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, watch, onUnmounted } from 'vue'
import QRCode from 'qrcode'
import { loadJs } from '@/utils/loadScript'
import { initLiveness, faceVerification } from '@/api/modules/settlement'
import { vab } from '@libs/utils'
import { useI18n } from "@/plugins/i18n";
import { t } from '@/domain/base'
export default {
  name: 'FaceVerificationDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'verify-complete', 'cancel'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const qrCertifyUrl = ref('')
    const loading = ref(false)
    const certifyUrl = ref('')
    const pollingTimer = ref(null)
    // 创建本地visible状态
    const visible = ref(props.visible)
    
    // 生成二维码
    const geneQrCode = async (certifyUrl) => {
      if (!certifyUrl) return
      try {
        loading.value = true
        qrCertifyUrl.value = await QRCode.toDataURL(certifyUrl, {
          width: 120,
          margin: 2,
          errorCorrectionLevel: 'H'
        })
      } catch (e) {
        console.log('generate qrcode error: ', e)
      } finally {
        loading.value = false
      }
    }
    
    // 初始化活体检测
    const initLivenessCheck = async () => {
      try {
        loading.value = true
        await loadJs(
          "https://cn-shanghai-aliyun-cloudauth.oss-cn-shanghai.aliyuncs.com/web_sdk_js/jsvm_all.js"
        );
        const metaInfo = window.getMetaInfo();
        const data = await initLiveness({
          metaInfo: JSON.stringify(metaInfo),
          // compUserId: '1234567890123'
        });
        // 模拟数据
        // const certifyUrl = 'http://www.baidu.com'
        if (data.certifyUrl) {
          certifyUrl.value = data.certifyUrl
          await geneQrCode(certifyUrl.value)
        }
      } catch (e) {
        console.log(e);
      } finally {
        loading.value = false
      }
    }
    
    // 开始轮询查询人脸认证状态
    const startPolling = () => {
      // 清除之前的定时器
      if (pollingTimer.value) {
        clearInterval(pollingTimer.value)
      }
      
      // 每1秒查询一次
      pollingTimer.value = setInterval(async () => {
        try {
          const res = await faceVerification();
          if (res.verifyResult) {
            // 人脸认证通过
            clearInterval(pollingTimer.value)
            pollingTimer.value = null
            emit('update:visible', false);
            emit('verify-complete');
          }
        } catch (error) {
          console.error('查询验证结果失败:', error);
        }
      }, 1000)
    }
    
    // 停止轮询
    const stopPolling = () => {
      if (pollingTimer.value) {
        clearInterval(pollingTimer.value)
        pollingTimer.value = null
      }
    }
    
    // 监听 props.visible 变化，当弹框显示时初始化
    watch(() => props.visible, (newVal) => {
      visible.value = newVal
      if (newVal) {
        initLivenessCheck()
        // 开始轮询
        startPolling()
      } else {
        // 重置状态
        qrCertifyUrl.value = ''
        loading.value = false
        // 停止轮询
        stopPolling()
      }
    })
    
    // 监听本地 visible 变化，当变化时通知父组件
    watch(visible, (newVal) => {
      emit('update:visible', newVal)
    })
    
    // 处理摄像头认证
    const handleCameraVerify = () => {
      // 在新tab页面中打开认证链接
      window.open(certifyUrl.value, '_blank');
    };
    
    // 处理取消
    const handleCancel = () => {
      emit('update:visible', false);
      emit('cancel');
    };
    
    // 处理认证完成
    const handleVerificationComplete = async () => {
      try {
        loading.value = true;
        // 调用faceVerification接口查询验证结果
        const res = await faceVerification();
        if (res.verifyResult) {
          // 人脸认证通过
          emit('update:visible', false);
          emit('verify-complete');
        } else {
          // 人脸认证未通过
          vab.baseMessage(t('settlement.toConfirm'), 'warning');
        }
      } catch (error) {
        console.error('查询验证结果失败:', error);
      } finally {
        loading.value = false;
      }
    };
    
    // 组件卸载时停止轮询
    onUnmounted(() => {
      stopPolling()
    })
    
    return {
      t,
      visible,
      qrCertifyUrl,
      loading,
      handleCameraVerify,
      handleCancel,
      handleVerificationComplete
    };
  }
};
</script>

<style scoped lang="scss"> 

.verification-title {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 20px;
  color: #303133;
}

.verification-method {
  margin-bottom: 24px;
}

.verification-method h4 {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
  color: #303133;
}

.qrcode-container {
  display: flex;
  justify-content: center;
  background: #F2F3F5;
  border-radius: 4px 4px 4px 4px;
  padding: 10px 0;
}

.qrcode {
  text-align: center;
}

.qrcode-image img {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  margin-bottom: 10px;
}

.qrcode-tip {
  margin-top: 12px;
  font-size: 12px;
  color: #606266;
}

.camera-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(242, 243, 245, 0.49);
  border-radius: 4px;
  padding: 6px 15px;
}

.camera-tip {
  margin: 0;
  font-size: 14px;
  color: #606266;
  line-height: 1;
}

.verify-button {
  margin: 0;
  height: 32px;
}

.verification-notes {
  margin-top: 20px;
  padding: 12px 16px;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
}

.verification-notes p {
  margin: 4px 0;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
}
</style>